import React, { useState } from "react";
import axios from "axios";
import { Phone, Mail, Send, Loader2 } from "lucide-react";
import { company, faqs } from "../mock";
import Reveal from "../components/Reveal";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import Seo from "../components/Seo";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const initialForm = { firstName: "", lastName: "", email: "", phone: "", message: "" };

const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thanks for submitting! We\u2019ll be in touch shortly.");
      setForm(initialForm);
    } catch (err) {
      toast.error("Sorry, something went wrong. Please call us on " + company.phone + ".");
    } finally {
      setLoading(false);
    }
  };

  const contactItems = [
    { icon: Phone, label: "Call us", value: company.phone, href: company.phoneHref },
    { icon: Mail, label: "Email us", value: company.email, href: `mailto:${company.email}` },
  ];

  return (
    <div className="overflow-hidden">
      <Seo
        title="Contact iClean NW | Free Cleaning Quote"
        description="Contact iClean NW for a free, no-obligation cleaning quote. Call 0161 667 1895 or send a message for commercial, office and industrial cleaning across Manchester and the North West."
        path="/contact"
      />
      <section className="pt-32 lg:pt-40 pb-4">
        <div className="max-w-6xl mx-auto px-5 lg:px-6">
          <span className="text-[#ea731d] font-semibold tracking-[0.2em] text-xs uppercase">Contact</span>
          <h1 className="mt-3 text-4xl lg:text-6xl font-bold text-[#1a1714] leading-[1.05] max-w-3xl">Let&rsquo;s get your space sparkling</h1>
          <p className="mt-5 text-lg text-neutral-600 max-w-2xl">Let us know what you are looking for and we will get back to you as soon as we can. Whether it&rsquo;s a small one-off clean or a long-term service contract, our friendly team is here to help businesses right across Manchester and the North West.</p>
          <p className="mt-3 text-base text-neutral-600 max-w-2xl">We cover commercial, office and industrial premises including shops, offices, warehouses, nurseries, schools and communal areas, as well as end-of-tenancy and post-construction cleans for houses, flats and commercial units. Call us on {company.phone}, email {company.email}, or complete the form below and we&rsquo;ll respond promptly with a free, no-obligation quote.</p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-6xl mx-auto px-5 lg:px-6 grid lg:grid-cols-5 gap-8">
          {/* Info cards */}
          <Reveal className="lg:col-span-2 space-y-4">
            {contactItems.map((c) => {
              const Icon = c.icon;
              const inner = (
                <div className="flex items-center gap-4 bg-white rounded-2xl p-5 border border-neutral-100 hover:border-[#ea731d]/40 hover:shadow-md transition-all">
                  <span className="w-12 h-12 rounded-xl bg-[#ea731d]/12 text-[#ea731d] flex items-center justify-center flex-shrink-0"><Icon size={22} /></span>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-neutral-500">{c.label}</div>
                    <div className="font-semibold text-[#1a1714]">{c.value}</div>
                  </div>
                </div>
              );
              return c.href ? (
                <a key={c.label} href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="block">{inner}</a>
              ) : (
                <div key={c.label}>{inner}</div>
              );
            })}
          </Reveal>

          {/* Form */}
          <Reveal delay={120} className="lg:col-span-3">
            <form onSubmit={handleSubmit} className="bg-white rounded-[28px] p-8 lg:p-10 border border-neutral-100 soft-shadow">
              <h2 className="text-2xl font-bold text-[#1a1714]">Send us a message</h2>
              <div className="mt-6 grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="firstName" className="text-sm font-medium text-[#1a1714]">First Name</Label>
                  <Input id="firstName" name="firstName" value={form.firstName} onChange={handleChange} className="mt-2 h-12 rounded-xl" placeholder="John" />
                </div>
                <div>
                  <Label htmlFor="lastName" className="text-sm font-medium text-[#1a1714]">Last Name</Label>
                  <Input id="lastName" name="lastName" value={form.lastName} onChange={handleChange} className="mt-2 h-12 rounded-xl" placeholder="Smith" />
                </div>
                <div>
                  <Label htmlFor="email" className="text-sm font-medium text-[#1a1714]">Email</Label>
                  <Input id="email" name="email" type="email" value={form.email} onChange={handleChange} className="mt-2 h-12 rounded-xl" placeholder="you@company.com" />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-sm font-medium text-[#1a1714]">Phone Number</Label>
                  <Input id="phone" name="phone" value={form.phone} onChange={handleChange} className="mt-2 h-12 rounded-xl" placeholder="0161 000 0000" />
                </div>
              </div>
              <div className="mt-5">
                <Label htmlFor="message" className="text-sm font-medium text-[#1a1714]">Message</Label>
                <Textarea id="message" name="message" value={form.message} onChange={handleChange} rows={5} className="mt-2 rounded-xl" placeholder="Tell us about the premises and what you need cleaned..." />
              </div>
              <button type="submit" disabled={loading} className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#ea731d] hover:bg-[#d9640f] disabled:opacity-70 text-white font-semibold px-8 py-3.5 rounded-full transition-all hover:-translate-y-0.5">
                {loading ? <><Loader2 size={18} className="animate-spin" /> Sending...</> : <><Send size={17} /> Send message</>}
              </button>
            </form>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-24">
        <div className="max-w-3xl mx-auto px-5 lg:px-6">
          <Reveal className="text-center">
            <span className="text-[#ea731d] font-semibold tracking-widest text-xs uppercase">FAQ</span>
            <h2 className="mt-3 text-3xl lg:text-4xl font-bold text-[#1a1714]">Frequently asked questions</h2>
          </Reveal>
          <Reveal delay={120} className="mt-10">
            <Accordion type="single" collapsible className="space-y-3">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="bg-white rounded-2xl border border-neutral-100 px-6">
                  <AccordionTrigger className="text-left font-semibold text-[#1a1714] hover:no-underline py-5">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-neutral-600 leading-relaxed pb-5">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Contact;
